import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Icon from '../ui/Icon';
// import { base44 } from '@/api/base44Client';

const propertyTypes = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'multi_family', label: 'Multi Family' },
  { value: 'land', label: 'Land' },
  { value: 'commercial', label: 'Commercial' }
];

const listingStatuses = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'sold', label: 'Sold' },
  { value: 'withdrawn', label: 'Withdrawn' }
];

export default function ListingForm({ open, onClose, listing, agents, onSave }) {
  const [formData, setFormData] = useState({
    title: '', address: '', city: '', state: '', zip_code: '',
    property_type: 'single_family', status: 'active', list_price: '',
    bedrooms: '', bathrooms: '', square_feet: '', lot_size: '',
    year_built: '', description: '', features: [], photos: [],
    agent_id: '', list_date: new Date().toISOString().split('T')[0], mls_number: ''
  });
  const [featureInput, setFeatureInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (listing) {
      setFormData({
        ...listing,
        list_price: listing.list_price || '',
        bedrooms: listing.bedrooms || '',
        bathrooms: listing.bathrooms || '',
        square_feet: listing.square_feet || '',
        lot_size: listing.lot_size || '',
        year_built: listing.year_built || '',
        features: listing.features || [],
        photos: listing.photos || []
      });
    } else if (open) {
      setFormData({
        title: '', address: '', city: '', state: '', zip_code: '',
        property_type: 'single_family', status: 'active', list_price: '',
        bedrooms: '', bathrooms: '', square_feet: '', lot_size: '',
        year_built: '', description: '', features: [], photos: [],
        agent_id: '', list_date: new Date().toISOString().split('T')[0], mls_number: ''
      });
    }
  }, [listing, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData(prev => ({ ...prev, features: [...prev.features, featureInput.trim()] }));
      setFeatureInput('');
    }
  };

  const removeFeature = (feature) => {
    setFormData(prev => ({ ...prev, features: prev.features.filter(f => f !== feature) }));
  };

  const handlePhotoUpload = async (e) => {
  const files = e.target.files;
  if (!files.length) return;

  setUploading(true);
  const newPhotos = [...formData.photos];

  for (const file of files) {
    // Simulate an uploaded file URL using a dummy URL or local blob
    const dummyUrl = URL.createObjectURL(file); // creates a temporary local URL
    newPhotos.push(dummyUrl);

    // Optional: simulate a short delay like an upload
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  setFormData(prev => ({ ...prev, photos: newPhotos }));
  setUploading(false);
};


  const removePhoto = (index) => {
    setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const dataToSave = {
      ...formData,
      list_price: formData.list_price ? Number(formData.list_price) : null,
      bedrooms: formData.bedrooms ? Number(formData.bedrooms) : null,
      bathrooms: formData.bathrooms ? Number(formData.bathrooms) : null,
      square_feet: formData.square_feet ? Number(formData.square_feet) : null,
      lot_size: formData.lot_size ? Number(formData.lot_size) : null,
      year_built: formData.year_built ? Number(formData.year_built) : null
    };
    await onSave(dataToSave);
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={listing ? 'Edit Listing' : 'New Listing'}>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-2 gap-4">
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Property Title</label>
            <input className="input" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Beautiful Family Home" required />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Address</label>
            <input className="input" value={formData.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="123 Main Street" required />
          </div>
          
          <div>
            <label className="label">City</label>
            <input className="input" value={formData.city} onChange={(e) => handleChange('city', e.target.value)} required />
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="label">State</label>
              <input className="input" value={formData.state} onChange={(e) => handleChange('state', e.target.value)} required />
            </div>
            <div className="flex-1">
              <label className="label">ZIP</label>
              <input className="input" value={formData.zip_code} onChange={(e) => handleChange('zip_code', e.target.value)} />
            </div>
          </div>
          
          <div>
            <label className="label">Property Type</label>
            <select className="input select" value={formData.property_type} onChange={(e) => handleChange('property_type', e.target.value)}>
              {propertyTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          
          <div>
            <label className="label">Status</label>
            <select className="input select" value={formData.status} onChange={(e) => handleChange('status', e.target.value)}>
              {listingStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          
          <div>
            <label className="label">List Price ($)</label>
            <input className="input" type="number" value={formData.list_price} onChange={(e) => handleChange('list_price', e.target.value)} placeholder="450000" required />
          </div>
          
          <div>
            <label className="label">MLS Number</label>
            <input className="input" value={formData.mls_number} onChange={(e) => handleChange('mls_number', e.target.value)} placeholder="MLS123456" />
          </div>
          
          <div>
            <label className="label">Bedrooms</label>
            <input className="input" type="number" value={formData.bedrooms} onChange={(e) => handleChange('bedrooms', e.target.value)} />
          </div>
          
          <div>
            <label className="label">Bathrooms</label>
            <input className="input" type="number" step="0.5" value={formData.bathrooms} onChange={(e) => handleChange('bathrooms', e.target.value)} />
          </div>
          
          <div>
            <label className="label">Square Feet</label>
            <input className="input" type="number" value={formData.square_feet} onChange={(e) => handleChange('square_feet', e.target.value)} />
          </div>
          
          <div>
            <label className="label">Year Built</label>
            <input className="input" type="number" value={formData.year_built} onChange={(e) => handleChange('year_built', e.target.value)} />
          </div>
          
          <div>
            <label className="label">Assigned Agent</label>
            <select className="input select" value={formData.agent_id} onChange={(e) => handleChange('agent_id', e.target.value)}>
              <option value="">Select agent</option>
              {agents?.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          
          <div>
            <label className="label">List Date</label>
            <input className="input" type="date" value={formData.list_date} onChange={(e) => handleChange('list_date', e.target.value)} />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Description</label>
            <textarea className="input textarea" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={3} placeholder="Describe the property..." />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Features</label>
            <div className="flex gap-2 mb-2">
              <input className="input" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} placeholder="Add a feature" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())} />
              <button type="button" className="btn btn-outline" onClick={addFeature}>Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.features.map((f, i) => (
                <span key={i} className="badge badge-slate" style={{ paddingLeft: '0.75rem' }}>
                  {f}
                  <button type="button" onClick={() => removeFeature(f)} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                    <Icon name="x" size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Photos</label>
            <div style={{ border: '2px dashed var(--slate-200)', borderRadius: '0.5rem', padding: '1rem' }}>
              <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} style={{ display: 'none' }} id="photo-upload" />
              <label htmlFor="photo-upload" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                {uploading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <Icon name="upload" size={32} style={{ color: 'var(--slate-400)', marginBottom: '0.5rem' }} />
                    <span className="text-sm text-slate-500">Click to upload photos</span>
                  </>
                )}
              </label>
            </div>
            {formData.photos.length > 0 && (
              <div className="grid grid-4 gap-2 mt-3">
                {formData.photos.map((url, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={url} alt="" style={{ width: '100%', height: '5rem', objectFit: 'cover', borderRadius: '0.375rem' }} />
                    <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', padding: '0.25rem', background: 'var(--red-500)', borderRadius: '50%', border: 'none', cursor: 'pointer', color: 'white' }}>
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer" style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <div className="spinner" style={{ borderTopColor: 'white' }} />}
            {listing ? 'Update Listing' : 'Create Listing'}
          </button>
        </div>
      </form>
    </Modal>
  );
}